import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import * as adminService from '../lib/services/adminService';
import * as orderService from '../lib/services/orderService';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    const saved = localStorage.getItem('munchups_coupon');
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch (error) {
      return null;
    }
  });
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('munchups_cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('munchups_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Persist the applied coupon so it carries over from the cart page to checkout
  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem('munchups_coupon', JSON.stringify(appliedCoupon));
    } else {
      localStorage.removeItem('munchups_coupon');
    }
  }, [appliedCoupon]);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const offers = await adminService.getPublicOffers();
        setActiveOffers(Array.isArray(offers) ? offers : []);
      } catch (error) {
        console.error('Error loading offers for cart pricing:', error);
        setActiveOffers([]);
      }
    };

    loadOffers();
  }, []);

  const pricing = useMemo(() => {
    const normalizedItems = cartItems.map((item) => {
      const baseUnitPrice = item.discounted_price && item.discounted_price < item.price
        ? Number(item.discounted_price)
        : Number(item.price || 0);
      const quantity = Math.max(0, parseInt(item.quantity, 10) || 0);
      return {
        ...item,
        base_unit_price: baseUnitPrice,
        quantity,
      };
    });

    const linePriceMap = new Map();
    normalizedItems.forEach((item) => {
      linePriceMap.set(item.id, {
        unit_price_before_offer: item.base_unit_price,
        unit_price_after_offer: item.base_unit_price,
        line_total_before_offer: item.base_unit_price * item.quantity,
        line_total_after_offer: item.base_unit_price * item.quantity,
        bundled_quantity: 0,
        bundled_line_total: 0,
      });
    });

    // Pool of purchasable units per category, sorted cheapest-first so bundles/discounts
    // are applied to the lowest-priced items first. Shared across offers so a category
    // can belong to several different mix & match offers without double-counting units.
    const poolsByCategory = new Map();
    normalizedItems.forEach((item) => {
      const categoryKey = String(item.category || '').trim().toLowerCase();
      if (!categoryKey) return;
      if (!poolsByCategory.has(categoryKey)) poolsByCategory.set(categoryKey, []);
      const pool = poolsByCategory.get(categoryKey);
      for (let i = 0; i < item.quantity; i++) {
        pool.push({ itemId: item.id, basePrice: Number(item.base_unit_price || 0) });
      }
    });
    poolsByCategory.forEach((pool) => pool.sort((a, b) => a.basePrice - b.basePrice));

    // A coupon and category offers are mutually exclusive - once a coupon is applied,
    // category offers are skipped entirely for pricing purposes.
    const normalizedOffers = appliedCoupon ? [] : (activeOffers || [])
      .map((offer) => {
        const offerType = String(offer.offer_type || 'bundle').trim().toLowerCase();
        const discountPercent = Number(offer.discount_percent || 0);
        const items = (offer.items || [])
          .map((item) => ({
            categoryKey: String(item.category || '').trim().toLowerCase(),
            quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
            categoryPrice: Number(item.category_price),
          }))
          .filter((item) => item.categoryKey);

        if (!items.length || !['bundle', 'discount'].includes(offerType)) return null;
        if (offerType === 'bundle' && items.some((item) => !Number.isFinite(item.categoryPrice) || item.categoryPrice <= 0)) return null;
        if (offerType === 'discount' && (discountPercent <= 0 || discountPercent > 100)) return null;

        return {
          id: offer.id,
          offer_type: offerType,
          title: offer.title || null,
          offer_price: offerType === 'bundle' ? items.reduce((sum, item) => sum + item.categoryPrice, 0) : null,
          discount_percent: discountPercent,
          currency: offer.currency || 'SEK',
          categories: items.map((item) => item.categoryKey),
          items,
        };
      })
      .filter(Boolean);

    const offerApplications = [];

    const applyOfferToLineTotals = (application) => {
      const { offer, candidateUnitsByCategory, targetTotalByCategory, candidateTotal, targetTotal } = application;

      candidateUnitsByCategory.forEach((units, categoryKey) => {
        const categoryCandidateTotal = units.reduce((sum, unit) => sum + unit.basePrice, 0);
        const categoryTargetTotal = offer.offer_type === 'bundle'
          ? targetTotalByCategory.get(categoryKey)
          : targetTotal * (categoryCandidateTotal / (candidateTotal || 1));
        const ratio = categoryCandidateTotal > 0 ? categoryTargetTotal / categoryCandidateTotal : 1;

        units.forEach((unit) => {
          const line = linePriceMap.get(unit.itemId);
          if (!line) return;
          const adjustedPrice = unit.basePrice * ratio;
          line.line_total_after_offer -= unit.basePrice;
          line.line_total_after_offer += adjustedPrice;
          if (offer.offer_type === 'bundle') {
            line.bundled_quantity += 1;
            line.bundled_line_total += adjustedPrice;
          }
        });
      });

      offerApplications.push({
        offer_id: offer.id,
        offer_type: offer.offer_type,
        title: offer.title,
        categories: offer.categories,
        offer_price: offer.offer_price,
        discount_percent: offer.discount_percent,
        currency: offer.currency,
        application_count: 1,
        total_savings: application.savings,
      });

      candidateUnitsByCategory.forEach((units, categoryKey) => {
        const consumed = new Set(units);
        const remainingPool = (poolsByCategory.get(categoryKey) || []).filter((unit) => !consumed.has(unit));
        poolsByCategory.set(categoryKey, remainingPool);
      });
    };

    const evaluateOffer = (offer) => {
      const candidateUnitsByCategory = new Map();
      const targetTotalByCategory = new Map();
      let candidateTotal = 0;
      let targetTotal = 0;
      let feasible = false;

      if (offer.offer_type === 'bundle') {
        feasible = true;
        for (const item of offer.items) {
          const pool = poolsByCategory.get(item.categoryKey) || [];
          if (pool.length < item.quantity) { feasible = false; break; }
          const chosen = pool.slice(0, item.quantity);
          candidateUnitsByCategory.set(item.categoryKey, chosen);
          candidateTotal += chosen.reduce((sum, unit) => sum + unit.basePrice, 0);
          targetTotalByCategory.set(item.categoryKey, item.categoryPrice);
          targetTotal += item.categoryPrice;
        }
      } else {
        for (const item of offer.items) {
          const pool = poolsByCategory.get(item.categoryKey) || [];
          if (pool.length > 0) feasible = true;
          candidateUnitsByCategory.set(item.categoryKey, pool.slice());
          candidateTotal += pool.reduce((sum, unit) => sum + unit.basePrice, 0);
        }
        targetTotal = candidateTotal * (1 - (offer.discount_percent / 100));
      }

      if (!feasible) return null;

      const savings = candidateTotal - targetTotal;
      return { offer, candidateUnitsByCategory, targetTotalByCategory, candidateTotal, targetTotal, savings };
    };

    const findBestApplicableOffer = (offersToEvaluate, preferMultiCategory = false) => {
      const evaluated = (offersToEvaluate || [])
        .map((offer) => evaluateOffer(offer))
        .filter(Boolean)
        .filter((application) => application.savings > 0)
        .sort((a, b) => {
          const aPriority = a.offer.items.length > 1 ? 2 : 1;
          const bPriority = b.offer.items.length > 1 ? 2 : 1;
          if (preferMultiCategory && aPriority !== bPriority) return bPriority - aPriority;
          return b.savings - a.savings;
        });

      return evaluated[0] || null;
    };

    const bundleOffers = normalizedOffers.filter((offer) => offer.offer_type === 'bundle');
    const discountOffers = normalizedOffers.filter((offer) => offer.offer_type === 'discount');

    if (bundleOffers.length > 0) {
      while (true) {
        const bestBundle = findBestApplicableOffer(bundleOffers, true);
        if (!bestBundle || bestBundle.savings <= 0) break;
        applyOfferToLineTotals(bestBundle);
      }
    } else {
      let iteration = 0;
      const maxSingleOfferIterations = 100;

      while (iteration < maxSingleOfferIterations) {
        const bestDiscount = findBestApplicableOffer(discountOffers, false);
        if (!bestDiscount || bestDiscount.savings <= 0) break;
        applyOfferToLineTotals(bestDiscount);
        iteration++;
      }
    }

    let subtotalBeforeOffers = 0;
    let subtotalAfterOffers = 0;
    let totalVAT = 0;

    const pricedItems = normalizedItems.map((item) => {
      const line = linePriceMap.get(item.id);
      const lineBefore = Number(line?.line_total_before_offer || 0);
      const lineAfter = Number(line?.line_total_after_offer || lineBefore);
      const adjustedUnitPrice = item.quantity > 0 ? lineAfter / item.quantity : Number(item.base_unit_price || 0);
      const vatPercent = Number(item.vat_percent || 0);
      const vatAmount = lineAfter * (vatPercent / (100 + vatPercent));

      subtotalBeforeOffers += lineBefore;
      subtotalAfterOffers += lineAfter;
      totalVAT += vatAmount;

      const bundledQty = line?.bundled_quantity ?? 0;
      const bundledLineTotal = Number(line?.bundled_line_total ?? 0);
      const unbundledQty = item.quantity - bundledQty;

      return {
        ...item,
        adjusted_unit_price: adjustedUnitPrice,
        line_total_before_offer: lineBefore,
        line_total_after_offer: lineAfter,
        bundled_quantity: bundledQty,
        bundled_line_total: bundledLineTotal,
        unbundled_quantity: unbundledQty,
        unbundled_unit_price: Number(item.base_unit_price || 0),
      };
    });

    const couponEligible = !appliedCoupon
      || subtotalAfterOffers >= (Number(appliedCoupon.min_purchase_amount) || 0);
    let couponDiscount = 0;
    if (appliedCoupon && couponEligible) {
      couponDiscount = String(appliedCoupon.discount_type).toLowerCase() === 'fixed'
        ? Number(appliedCoupon.discount_value) || 0
        : subtotalAfterOffers * ((Number(appliedCoupon.discount_value) || 0) / 100);
      couponDiscount = Math.max(0, Math.min(couponDiscount, subtotalAfterOffers));
    }

    // Spread the coupon discount proportionally across each item's post-offer line total
    // so the per-item price shown (and sent to the backend on checkout) already reflects it.
    const couponAdjustedItems = couponDiscount > 0
      ? pricedItems.map((item) => {
        const lineShare = subtotalAfterOffers > 0 ? item.line_total_after_offer / subtotalAfterOffers : 0;
        const lineTotalAfterCoupon = Math.max(0, item.line_total_after_offer - (couponDiscount * lineShare));
        return {
          ...item,
          adjusted_unit_price: item.quantity > 0 ? lineTotalAfterCoupon / item.quantity : item.adjusted_unit_price,
          line_total_after_offer: lineTotalAfterCoupon,
        };
      })
      : pricedItems;

    const finalTotal = Math.max(0, subtotalAfterOffers - couponDiscount);
    const finalVAT = subtotalAfterOffers > 0
      ? totalVAT * (finalTotal / subtotalAfterOffers)
      : 0;

    return {
      pricedItems: couponAdjustedItems,
      subtotalBeforeOffers,
      subtotalAfterOffers,
      offerDiscount: Math.max(0, subtotalBeforeOffers - subtotalAfterOffers),
      offerApplications,
      totalVAT: finalVAT,
      appliedCoupon,
      couponEligible,
      couponDiscount,
      finalTotal,
    };
  }, [cartItems, activeOffers, appliedCoupon]);

  const addToCart = (product, quantity = 1) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      
      if (existingItem) {
        // Update quantity if product already in cart
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new product to cart
        return [...prevItems, { ...product, quantity }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId)
    );
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedCoupon(null);
    setCouponError(null);
    // Clear immediately rather than waiting on the persistence effect, so a coupon
    // never carries over into the next order placed in the same browser session.
    localStorage.removeItem('munchups_coupon');
  };

  const applyCoupon = async (code) => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) {
      setCouponError('Please enter a coupon code');
      return false;
    }

    setCouponLoading(true);
    setCouponError(null);
    try {
      const result = await orderService.validateCoupon(trimmedCode, pricing.subtotalBeforeOffers);
      setAppliedCoupon({
        code: result.code,
        discount_type: result.discount_type,
        discount_value: result.discount_value,
        min_purchase_amount: result.min_purchase_amount,
      });
      return true;
    } catch (error) {
      setAppliedCoupon(null);
      setCouponError(error.message || 'Failed to apply coupon');
      return false;
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
    localStorage.removeItem('munchups_coupon');
  };

  const getTotalPrice = () => {
    return pricing.finalTotal;
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalVAT = () => {
    return pricing.totalVAT;
  };

  const getCartPricing = () => pricing;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
        getTotalVAT,
        getCartPricing,
        appliedCoupon,
        applyCoupon,
        removeCoupon,
        couponError,
        couponLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
